/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceSupabase - A library for working with supabase made in Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.
*/

import { Injectable, effect, inject } from "@angular/core";
import { Router } from "@angular/router";
import { CbRoutesUtils } from "@app/@classes/routes/cb-routes-utils";
import { from, map, tap } from "rxjs";
import { environment } from "src/environments/environment";
import { Supabase } from "../../@classes/supabase";
import { TenantUsersService } from "../data/tenant-users.service";
import { AuthStateService } from "./auth-state.service";
import { LoggerService } from "./logger.service";

const TABLE = "tenants";
type T = typeof TABLE;

@Injectable({
	providedIn: "root",
})
export class AuthTenantService extends Supabase {
	private readonly router: Router = inject(Router);
	private readonly loggerService: LoggerService = inject(LoggerService);
	private readonly authStateService: AuthStateService =
		inject(AuthStateService);
	public readonly tenantUsersService = inject(TenantUsersService);

	constructor() {
		super();

		effect(() => {
			this.refreshTenant();
		});

		this.supabaseClient
			.channel("tenant_info")
			.on(
				"postgres_changes",
				{
					event: "*",
					schema: "public",
					table: "tenants",
				},
				(data) => {
					this.refreshTenant();
				},
			)
			.subscribe();
	}

	public changeTenant() {
		this.authStateService.currentTenantId.set(undefined);
		this.router.navigate([CbRoutesUtils.paths.tenant.path]);
	}

	public refreshTenant() {
		this.loadTenantInfo();
		this.loadTenantRole();
	}

	public selectTenant(id: string) {
		this.authStateService.currentTenantId.set(id);
		const role = this.authStateService.currentTenantRole();

		if (role === "warehouseman") {
			this.router.navigate([CbRoutesUtils.paths.w.palmtop.path]);
		} else {
			this.router.navigate([CbRoutesUtils.paths.choose_office.path]);
		}
	}

	private loadTenantInfo() {
		const id = this.authStateService.currentTenantId();

		if (!id) return;

		from(this.supabaseClient.from("tenants").select("*").eq("id", id))
			.pipe(
				map((response) => {
					if (response.error) {
						throw response.error;
					}

					return response.data[0];
				}),
			)
			.subscribe((tenant) => {
				if (!tenant) return;
				this.authStateService.currentTenantInfo.set(tenant);
			});
	}

	private loadTenantRole() {
		const tenantId = this.authStateService.currentTenantId();
		const userId = this.authStateService.currentUserId();

		if (!tenantId || !userId) return;

		from(
			this.supabaseClient
				.from("tenant_users")
				.select("*")
				.eq("tenant_id", tenantId)
				.eq("user_id", userId),
		)
			.pipe(
				tap((response) => {
					if (response.error) throw response.error;

					const data = response.data[0];

					this.authStateService.currentTenantRole.set(data?.role);
				}),
			)
			.subscribe();
	}

	/**
	 * Loads the tenants of the current logged in user.
	 * If the user is not logged in, the method does nothing.
	 * If the user has no tenants, the method redirects to the tenant creation page.
	 * If the user has one tenant, the method redirects to the homepage.
	 * If the user has multiple tenants, the method redirects to the tenant selection page.
	 */
	public checkAndNavigateTenancy(): void {
		const userId = this.authStateService.currentUserId();
		this.loggerService.logInfo("checkAndNavigateTenancy", { userId });

		if (!userId) return;

		this.tenantUsersService
			.getMany({
				filters: {
					user_id: {
						active: true,
						column: "user_id",
						value: userId,
						operator: "eq",
					},
					status: {
						active: true,
						column: "invitation_status",
						value: "accepted",
						operator: "eq",
					},
				},
			})
			.subscribe((tenants) => {
				console.log("tenants", tenants);
				if (tenants.length === 0) {
					this.loggerService.logInfo(
						"User has no tenants, redirecting to user onboarding page.",
					);

					if (this.authStateService.isSuperadmin()) {
						this.router.navigate([CbRoutesUtils.paths.backoffice.path]);
					} else {
						if (environment.canCreateTenants) {
							this.router.navigate([
								CbRoutesUtils.paths.tenant.onboarding.path,
							]);
						} else {
							this.router.navigate([
								CbRoutesUtils.paths.tenant.invitations.path,
							]);
						}
					}
				} else if (tenants.length === 1) {
					this.loggerService.logInfo(
						"User has only one tenant, redirecting to home page.",
					);
					const first = tenants[0].tenant_id;

					if (first && first !== undefined) {
						this.authStateService.currentTenantRole.set(tenants[0].role);
						this.selectTenant(first);
					} else {
						this.router.navigate([CbRoutesUtils.paths.tenant.onboarding.path]);
					}
				} else {
					this.loggerService.logInfo(
						"User has multiple tenants, redirecting to tenant selection page.",
					);
					this.router.navigate([CbRoutesUtils.paths.tenant.path]);
				}
			});
	}
}
